import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '~/shared/api/queryKeys';
import {
    type PayinAdminTraderV1BlockTraderRequest,
    type PayinAdminTraderV1ActivateTraderRequest,
    type PayinAdminTraderV1UnblockTraderRequest,
    type AdminPayinTraderServiceListTradersParams,
    type PayinAdminTraderV1UpdateTraderConfigRequest,
    type AdminPayinTraderServiceGetTraderParams,
} from '~/generated/payinBackend/model';
import {
    adminPayinTraderServiceActivateTrader,
    adminPayinTraderServiceBlockTrader,
    adminPayinTraderServiceGetTrader,
    adminPayinTraderServiceListTraders,
    adminPayinTraderServiceUnblockTrader,
    adminPayinTraderServiceUpdateTraderConfig,
    getAdminPayinTraderServiceGetTraderUrl,
    getAdminPayinTraderServiceListTradersUrl,
} from '~/generated/payinBackend/trader';
import { type TraderStatus } from '../model';

interface UseTraderMutationProps {
    onError?: (error: Error) => void;
    onSuccess?: () => void;
}

export const useActivateTrader = (options?: UseTraderMutationProps) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: PayinAdminTraderV1ActivateTraderRequest) => {
            return await adminPayinTraderServiceActivateTrader(payload);
        },
        onError: options?.onError,
        onSuccess: () => {
            queryClient
                .invalidateQueries({
                    queryKey: [queryKeys.PAYIN_TRADERS],
                })
                .then(() => {
                    options?.onSuccess?.();
                    notifications.show({
                        color: 'green',
                        title: 'Успешно',
                        message: 'Аккаунт активирован',
                    });
                });
        },
    });
};

export const useBlockTrader = (options?: UseTraderMutationProps) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: PayinAdminTraderV1BlockTraderRequest) => {
            return await adminPayinTraderServiceBlockTrader(payload);
        },
        onError: options?.onError,
        onSuccess: () => {
            queryClient
                .invalidateQueries({
                    queryKey: [queryKeys.PAYIN_TRADERS],
                })
                .then(() => {
                    options?.onSuccess?.();
                    notifications.show({
                        color: 'green',
                        title: 'Успешно',
                        message: 'Аккаунт заблокирован',
                    });
                });
        },
    });
};

export const useUnBlockTrader = (options?: UseTraderMutationProps) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: PayinAdminTraderV1UnblockTraderRequest) => {
            return await adminPayinTraderServiceUnblockTrader(payload);
        },
        onError: options?.onError,
        onSuccess: () => {
            queryClient
                .invalidateQueries({
                    queryKey: [queryKeys.PAYIN_TRADERS],
                })
                .then(() => {
                    options?.onSuccess?.();
                    notifications.show({
                        color: 'green',
                        title: 'Успешно',
                        message: 'Аккаунт заблокирован',
                    });
                });
        },
    });
};

type TradersParams = Omit<AdminPayinTraderServiceListTradersParams, 'filter.statuses'> & {
    'filter.statuses'?: TraderStatus[];
};

export const useTradersList = (params?: TradersParams, refetchInterval?: number | false) => {
    return useQuery({
        refetchInterval,
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        queryFn: () => adminPayinTraderServiceListTraders(params as AdminPayinTraderServiceListTradersParams),
        queryKey: [
            queryKeys.PAYIN_ORDERS,
            getAdminPayinTraderServiceListTradersUrl(params as AdminPayinTraderServiceListTradersParams),
        ],
    });
};

export const useUpdateTraderConfig = (options?: UseTraderMutationProps) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: PayinAdminTraderV1UpdateTraderConfigRequest) => {
            return await adminPayinTraderServiceUpdateTraderConfig(payload);
        },
        onError: options?.onError,
        onSuccess: () => {
            queryClient
                .invalidateQueries({
                    queryKey: [queryKeys.PAYIN_TRADERS],
                })
                .then(() => {
                    options?.onSuccess?.();
                    notifications.show({
                        color: 'green',
                        title: 'Успешно',
                        message: 'Конфигурация обновлена',
                    });
                });
        },
    });
};

export const useTrader = (params: AdminPayinTraderServiceGetTraderParams) => {
    return useQuery({
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        queryFn: () => adminPayinTraderServiceGetTrader(params),
        queryKey: [queryKeys.PAYIN_TRADERS, queryKeys.PAYIN_TRADER, getAdminPayinTraderServiceGetTraderUrl(params)],
    });
};
